/**
 * The equipment slot registry.
 *
 * One import and one entry per slot, and nothing else. `SLOT_ORDER` is not decoration: it is the
 * order `aggregateLoadout` walks, so two peers holding the same equipment sum the same armour in
 * the same sequence and land on the same number.
 */

import { ACCESSORY_1_SLOT } from '@/sim/inventory/slots/accessory1';
import { ACCESSORY_2_SLOT } from '@/sim/inventory/slots/accessory2';
import { ACCESSORY_3_SLOT } from '@/sim/inventory/slots/accessory3';
import { HEAD_SLOT } from '@/sim/inventory/slots/head';
import { LEGS_SLOT } from '@/sim/inventory/slots/legs';
import { PRIMARY_WEAPON_SLOT } from '@/sim/inventory/slots/primaryWeapon';
import { SIDEARM_SLOT } from '@/sim/inventory/slots/sidearm';
import { TORSO_SLOT } from '@/sim/inventory/slots/torso';
import type { EquipmentSlot, SlotId } from '@/sim/inventory/slotContract';

export const SLOTS = {
  primaryWeapon: PRIMARY_WEAPON_SLOT,
  sidearm: SIDEARM_SLOT,
  head: HEAD_SLOT,
  torso: TORSO_SLOT,
  legs: LEGS_SLOT,
  accessory1: ACCESSORY_1_SLOT,
  accessory2: ACCESSORY_2_SLOT,
  accessory3: ACCESSORY_3_SLOT,
} as const satisfies Record<SlotId, EquipmentSlot>;

/** Aggregation order, and part of the determinism contract. */
export const SLOT_ORDER: readonly SlotId[] = [
  'primaryWeapon',
  'sidearm',
  'head',
  'torso',
  'legs',
  'accessory1',
  'accessory2',
  'accessory3',
];

/** Slot lookup, total over the union so it never widens to `undefined`. */
export function equipmentSlot(id: SlotId): EquipmentSlot {
  return SLOTS[id];
}

/** Whether `value` names a slot. Used when reading equipment back out of a save. */
export function isSlotId(value: string): value is SlotId {
  return Object.hasOwn(SLOTS, value);
}
